import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getLivePostStats, listPostsBySlugs, listServices } from "@/lib/public.functions";
import { getBousalaBuyContext } from "@/lib/bousala-purchase.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { TestimonialCard, testimonialsQuery } from "@/components/testimonials-strip";
import { eyebrow, btn } from "@/components/ds/styles";
import { FiguresBlock } from "@/components/ds/figures-block";
import { Faqs, homeFaqs } from "@/components/landing/faqs";
import { useSmartAdminPreview } from "@/components/bousala/smart/smart-preview";
import { SituationPicker, type HomePost } from "@/components/home/situation-picker";
import { HOME_CARD_SLUGS, HOME_POST_SLUGS, HOME_SITUATIONS } from "@/data/home-situations";
import { TONE_CLASS, ageRange, firstRakiza, rakizaTone } from "@/lib/home-format";
import { Section, cardOn } from "@/components/ds/section";
import { InstagramSection } from "@/components/home/instagram-section";
import { DrSaraMessage } from "@/components/home/dr-sara-message";
import { NextStepCta } from "@/components/home/next-step-cta";
import { VideoPlayerProvider } from "@/components/home/video-player";
import { VideoCard } from "@/components/home/video-card";
import { HomeVideosSection } from "@/components/home/home-videos-section";
import { MobileBookingBar } from "@/components/home/mobile-booking-bar";
import { BOUSALA_SHORT, TOP_SHORT, formatViewsShortLatin } from "@/data/home-videos";
import diplomasB from "@/assets/dr-sara-diplomas-b.jpg.asset.json";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { supportWhatsAppUrl } from "@/lib/social";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";

const servicesQ = () =>
  queryOptions({ queryKey: ["public", "services"], queryFn: () => listServices() });
const smartCtxQ = () =>
  queryOptions({
    queryKey: ["bousala", "buy-context", "smart"],
    queryFn: () => getBousalaBuyContext({ data: { product: "smart" } }),
    staleTime: 60_000,
  });
const homePostsQ = () =>
  queryOptions({
    queryKey: ["public", "posts", "by-slugs", HOME_POST_SLUGS],
    queryFn: () => listPostsBySlugs({ data: { slugs: HOME_POST_SLUGS } }),
  });
const postStatsQ = () =>
  queryOptions({ queryKey: ["public", "posts", "stats"], queryFn: () => getLivePostStats() });

const HOME_DESCRIPTION =
  "استشارات تربوية فردية أونلاين أو في العيادة، مدوّنة أسبوعية، والبوصلة لاكتشاف التخصص الأقرب لك — منصّة د. سارة النحّاس.";

export const Route = createFileRoute("/_public/")({
  head: ({ loaderData }) => {
    const d = loaderData as { smartActive?: boolean; duration?: number; smartPrice?: number | null } | undefined;
    return {
      meta: [
        { title: "د. سارة النحّاس — منصّة التربية والعلاقة الآمنة" },
        { name: "description", content: HOME_DESCRIPTION },
        { property: "og:title", content: "د. سارة النحّاس — منصّة التربية والعلاقة الآمنة" },
        { property: "og:description", content: HOME_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SY" },
        { property: "og:site_name", content: "د. سارة النحّاس" },
        ...ogImageMeta(),
        ...canonicalMeta("/"),
      ],
      links: [canonicalLink("/")],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: homeFaqs(Boolean(d?.smartActive), {
              duration: d?.duration ?? "",
              smartPrice: d?.smartPrice ?? null,
            }).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  loader: async ({ context }) => {
    const [services, smartCtx] = await Promise.all([
      context.queryClient.ensureQueryData(servicesQ()),
      context.queryClient.ensureQueryData(smartCtxQ()),
      context.queryClient.ensureQueryData(homePostsQ()),
      context.queryClient.ensureQueryData(postStatsQ()),
    ]);
    const consult = (services ?? []).find((s: any) => s.slug === "initial-consultation") ?? services?.[0];
    const product = (smartCtx as any)?.product ?? null;
    return {
      smartActive: Boolean(product),
      duration: consult?.duration_min ?? null,
      smartPrice: product ? Number(product.price_usd) : null,
    };
  },
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: Home,
});

const usd = (v: unknown) => (v == null ? "" : `${Number(v).toFixed(0)}$`);

function Home() {
  const { data: services } = useSuspenseQuery(servicesQ());
  const { data: smartCtx } = useSuspenseQuery(smartCtxQ());
  const { data: postRows } = useSuspenseQuery(homePostsQ());
  const { data: stats } = useSuspenseQuery(postStatsQ());
  const { data: testimonials } = useQuery(testimonialsQuery());
  const { product: smart } = useSmartAdminPreview((smartCtx as any)?.product ?? null);

  const consult = services?.find((s: any) => s.slug === "initial-consultation") ?? services?.[0] ?? null;
  const pkg = services?.find((s: any) => s.slug === "monthly-package-4") ?? null;
  const duration = consult?.duration_min ?? "";
  const posts: Record<string, HomePost> = Object.fromEntries(
    ((postRows ?? []) as HomePost[]).map((p) => [p.slug, p]),
  );
  const cards = HOME_CARD_SLUGS.map((s) => posts[s]).filter(Boolean);
  const faqs = homeFaqs(Boolean(smart), {
    duration,
    smartPrice: smart ? Number((smart as any).price_usd) : null,
  });
  const quotes = ((testimonials ?? []) as any[]).filter((t) => t.is_featured !== false).slice(0, 3);
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  const [selected, setSelected] = useState<string | null>(null);
  const picked = HOME_SITUATIONS.find((x) => x.slug === selected && posts[x.slug]) ?? null;
  const whatsappUrl = settings?.clinic_whatsapp?.trim() ? supportWhatsAppUrl(settings.clinic_whatsapp) : null;

  return (
    <VideoPlayerProvider>
      {/* 1. Hero */}
      <Section tone="cream" inner="mx-auto max-w-[76rem] px-5 pb-14 pt-6 lg:pb-20 lg:pt-14">
        <SituationPicker situations={HOME_SITUATIONS} posts={posts} duration={duration} selected={selected} onSelect={setSelected} whatsappUrl={whatsappUrl} />
      </Section>

      {/* 2. Where to start */}
      <Section tone="white" inner="mx-auto max-w-[76rem] px-5 py-14 lg:py-20">
        <p className={eyebrow}>ثلاث طرق</p>
        <h2 className="mt-2 font-display text-[30px] font-extrabold text-maroon-500 lg:text-[36px]">من وين نبلّش؟</h2>
        <div className="mt-6 grid gap-3.5 lg:grid-cols-12 lg:grid-rows-2">
          <Link to="/book" className="lift flex flex-col overflow-hidden rounded-ds-xl border border-hairline-subtle bg-surface shadow-sm lg:col-span-6 lg:row-span-2">
            <div className="relative">
              <img
                src={diplomasB.url}
                alt="د. سارة في عيادتها أمام شهاداتها"
                width={900}
                height={600}
                loading="lazy"
                className="h-[176px] w-full object-cover lg:h-[280px]"
              />
              <span className="absolute start-3 top-3 rounded-ds-pill bg-surface px-3 py-1.5 font-display text-[13px] font-bold text-text-primary shadow-sm">
                أونلاين أو بالعيادة · أبو رمانة
              </span>
            </div>
            <div className="flex flex-1 flex-col px-[22px] py-6">
              {consult && (
                <p className="flex flex-wrap items-baseline gap-x-3">
                  <span className="font-display text-[44px] font-extrabold leading-none text-amber-800">{usd(consult.price_usd)}</span>
                  <span className="text-[14px] text-text-secondary">للجلسة{pkg ? ` · 4 جلسات بـ ${usd(pkg.price_usd)}` : ""}</span>
                </p>
              )}
              <h3 className="mt-4 font-display text-[23px] font-extrabold text-text-primary lg:text-[26px]">استشارة فردية مع د. سارة</h3>
              <p className="mt-2 text-[15px] leading-[1.8] text-text-secondary lg:text-[16px]">
                {`${duration} دقيقة، أونلاين من أي بلد أو بالعيادة، وخطة عملية لبيتك. اليوم والساعة بنتفق عليهم على واتساب.`}
              </p>
              <p className="mt-3 text-[13px] text-text-secondary">الدفع: PayPal أو شام كاش أو حوالة الهرم · كاش بالعيادة</p>
              <span className="mt-auto pt-5">
                <span className="inline-flex min-h-12 items-center rounded-ds-pill bg-primary px-6 font-display text-[16px] font-bold text-primary-foreground">حجز استشارة ←</span>
              </span>
            </div>
          </Link>

          {quotes[0] && (
            <div className="lg:hidden">
              <TestimonialCard t={quotes[0]} i={1} className="bg-teal-100" />
            </div>
          )}

          <div className="flex gap-4 rounded-ds-xl bg-amber-100 p-5 lg:col-span-6">
            <Link to="/bousala" className="flex min-w-0 flex-1 flex-col">
              <span className="self-start rounded-ds-pill bg-surface/75 px-3 py-1 font-display text-[13px] font-bold text-green-800">مجاناً</span>
              <span className="mt-3 font-display text-[23px] font-extrabold text-text-primary">البوصلة الدراسية</span>
              <span className="mt-2 text-[15px] leading-[1.8] text-amber-800">لابنك أو بنتك من 15 لـ 35 · لاختيار التخصص · التقرير فوراً</span>
              <span className="mt-auto inline-flex min-h-11 items-end pt-3 font-display text-[16px] font-bold text-amber-800">بدء البوصلة ←</span>
            </Link>
            <VideoCard video={BOUSALA_SHORT} clamp={2} label="فيديو قصير" className="h-[168px] w-[112px] lg:h-[204px] lg:w-[136px]" titleSize="text-[13px]" />
          </div>

          <Link to="/blog" className="lift flex flex-col rounded-ds-xl bg-blue-100 p-5 lg:col-span-6">
            <p className="flex items-baseline gap-2">
              <span className="font-display text-[44px] font-extrabold leading-none text-blue-700">{stats.count}</span>
              <span className="font-display text-[17px] font-bold text-blue-700">مقال حسب عمر طفلك</span>
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["1–2", "2–3", "3–4", "4–6", "6–8", "8–10"].map((p) => (
                <span key={p} dir="ltr" className="rounded-ds-pill bg-surface/75 px-3 py-1 font-display text-[13px] font-bold text-text-primary">{p}</span>
              ))}
            </div>
            <span className="mt-auto inline-flex min-h-11 items-end pt-3 font-display text-[16px] font-bold text-blue-700">القراءة ←</span>
          </Link>
        </div>
        <p className="mt-5 flex flex-wrap items-center gap-2 text-[14px] text-text-secondary">
          <span className="rounded-ds-pill bg-cream-100 px-3 py-1 font-display text-[13px] font-bold">قريباً</span>
          البوصلة التربوية لعمر 1–9، ودورات قصيرة عن صدمات الطفولة.
        </p>
      </Section>

      {/* 3. Videos */}
      <Section tone="cream">
        <HomeVideosSection />
      </Section>

      {/* 4. Message band */}
      <Section tone="white" className="py-14 lg:py-20">
        <InstagramSection instagramSetting={settings?.instagram_url} />
      </Section>

      <Section tone="cream" aria-label="رسالة من د. سارة">
        <DrSaraMessage duration={duration} />
      </Section>

      {/* 5. Figures */}
      <Section tone="white" inner="reveal mx-auto max-w-[76rem] px-5 py-14 lg:py-20">
        <p className={eyebrow}>الأمان قبل الانضباط</p>
        <h2 className="mt-2 font-display text-[26px] font-extrabold text-maroon-500 lg:text-[30px]">أرقام من الطريق</h2>
        <div className="mt-6">
          <FiguresBlock tone="white" omit={["1M+"]} extra={[[formatViewsShortLatin(TOP_SHORT.views), "مشاهدة لمقطع واحد على يوتيوب"]]} />
        </div>
      </Section>

      {/* 6. Posts */}
      {cards.length > 0 && (
        <Section tone="cream" inner="reveal mx-auto max-w-[76rem] py-14 lg:py-20">
          <div className="flex items-end justify-between gap-6 px-5">
            <div>
              <p className={eyebrow}>من المدوّنة</p>
              <h2 className="mt-2 font-display text-[30px] font-extrabold text-maroon-500 lg:text-[36px]">3 مقالات لحالتك</h2>
            </div>
            <div className="hidden lg:block"><ReadMoreLink /></div>
          </div>
          <div className="mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 [scrollbar-width:none] lg:grid lg:grid-cols-3 lg:overflow-visible [&::-webkit-scrollbar]:hidden">
            {cards.map((p) => {
              const rk = firstRakiza(p.rakiza);
              const age = ageRange(p.age_band);
              return (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className={"lift w-[300px] shrink-0 snap-start rounded-ds-xl border border-hairline-subtle p-3 shadow-sm lg:w-auto " + cardOn("cream")}
                >
                  {p.og_image ? (
                    <img src={p.og_image} alt={p.image_alt ?? ""} loading="lazy" width={600} height={315} className="h-[150px] w-full rounded-ds-lg object-cover lg:h-[180px]" />
                  ) : (
                    <div className="h-[150px] rounded-ds-lg bg-cream-100 lg:h-[180px]" />
                  )}
                  <div className="px-2 pb-2 pt-4">
                    {(rk || p.reading_min) && (
                      <div className="flex items-center justify-between gap-3">
                        {rk && (
                          <span className={"inline-block rounded-ds-pill px-3 py-1 font-display text-[13px] font-bold " + TONE_CLASS[rakizaTone(rk)]}>{rk}</span>
                        )}
                        {p.reading_min ? <span className="shrink-0 text-[13px] text-text-secondary lg:text-[14px]">{p.reading_min} دقيقة</span> : null}
                      </div>
                    )}
                    <h3 className="mt-3 font-display text-[17px] font-bold leading-snug text-text-primary lg:text-[19px]">{p.title_ar}</h3>
                    {age && <p className="mt-3 text-[13px] text-text-secondary"><bdi dir="ltr">{age}</bdi> سنوات</p>}
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-4 px-5 lg:hidden">
            <ReadMoreLink />
          </div>
        </Section>
      )}

      {/* 7. Testimonials + FAQ */}
      <Section tone="white" className="py-14 lg:py-20">
        <div className="grid gap-14 lg:mx-auto lg:max-w-[76rem] lg:grid-cols-2 lg:gap-6">
          {quotes.length > 0 && (
            <section className="min-w-0 lg:order-2">
              <div className="px-5 lg:px-0">
                <p className="font-display text-[13px] font-bold text-blue-700">قيل عن د. سارة</p>
                <h2 className="mt-2 font-display text-[26px] font-extrabold text-maroon-500 lg:text-[30px]">بيوت صارت أهدأ</h2>
              </div>
              <div
                role="region"
                tabIndex={0}
                aria-label="آراء الأهل"
                className="mt-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2 [scrollbar-width:none] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50 lg:flex-col lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden"
              >
                {quotes.map((t, i) => (
                  <div key={t.id} className={"w-[300px] shrink-0 snap-start lg:w-auto " + (i === 0 ? "hidden lg:block" : "")}>
                    <TestimonialCard t={t} i={i} />
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className={"mx-3 min-w-0 rounded-ds-2xl border border-hairline-subtle px-5 py-9 lg:order-1 lg:mx-0 lg:px-10 " + cardOn("white")}>
            <p className={eyebrow}>قبل الحجز</p>
            <h2 className="mb-4 mt-2 font-display text-[26px] font-extrabold text-maroon-500 lg:text-[30px]">أسئلة تهمّك</h2>
            <Faqs items={faqs} />
          </section>
        </div>
      </Section>

      {/* 8. Next step */}
      <Section tone="cream" className="py-14 lg:py-20">
        <NextStepCta
          situation={picked}
          duration={duration}
          price={consult ? usd(consult.price_usd) : ""}
          pkgPrice={pkg ? usd(pkg.price_usd) : ""}
          whatsappUrl={whatsappUrl}
        />
      </Section>

      <MobileBookingBar whatsappUrl={whatsappUrl} />
    </VideoPlayerProvider>
  );
}

function ReadMoreLink({ className = "" }: { className?: string }) {
  return (
    <Link to="/blog" className={btn.link + " " + className}>
      متابعة القراءة حسب عمر طفلك ←
    </Link>
  );
}
